import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import { useAppContext } from "../../../context";
import {
  X, Plus, ShoppingCart, Heart, Search, Star,
  CheckCircle, XCircle, ArrowLeft,
} from "lucide-react";
import Footer from "../components/Footer";

// ── Fetch ─────────────────────────────────────────────────────────────────────

const fetchAllProducts = async () => {
  const { data } = await axios.get(`${apiUrl}/menuItem`);
  return data.list || [];
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const MAX_COMPARE = 3;

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return <span className="text-gray-300">—</span>;
  if (typeof v === "boolean") return v ? <CheckCircle size={16} className="text-green-500 mx-auto" /> : <XCircle size={16} className="text-red-400 mx-auto" />;
  return v;
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StarRow = ({ rating }) => (
  <span className="flex items-center justify-center gap-0.5">
    {[1, 2, 3, 4, 5].map(s => (
      <span key={s} className={s <= Math.round(rating || 0) ? "text-amber-400 text-sm" : "text-gray-200 text-sm"}>★</span>
    ))}
  </span>
);

// Product picker: search + click to add
const ProductPicker = ({ products, selected, onAdd, onClose }) => {
  const [q, setQ] = useState("");
  const results = products
    .filter(p => !selected.includes(p.id) && p.name.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 12);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-900">Add to Compare</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search items…"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-3 space-y-1">
          {results.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-8">No items found.</p>
          )}
          {results.map(p => (
            <button
              key={p.id}
              onClick={() => { onAdd(p.id); onClose(); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-orange-50 transition text-left"
            >
              <img
                src={p.images?.image || p.image}
                alt={p.name}
                className="w-12 h-12 object-cover rounded-xl flex-shrink-0 bg-gray-100"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                <p className="text-xs text-gray-400 truncate">{p.category?.name}</p>
              </div>
              <span className="text-sm font-bold text-orange-500 flex-shrink-0">{p.price}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Empty slot placeholder
const EmptySlot = ({ onAdd }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-3 p-6 border-2 border-dashed border-gray-200 rounded-2xl text-center bg-gray-50/50">
    <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
      <Plus size={22} className="text-orange-500" />
    </div>
    <p className="text-sm font-semibold text-gray-500">Add item to compare</p>
    <button
      onClick={onAdd}
      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition"
    >
      Browse Items
    </button>
  </div>
);

// ── Row definitions ───────────────────────────────────────────────────────────

const ROWS = [
  { label: "Category",    key: (p)    => p.category?.name  },
  { label: "Price",       key: (p)    => p.price, highlight: true },
  { label: "Rating",      key: (p, r) => r ? <StarRow rating={r.average} /> : <span className="text-gray-300">—</span> },
  { label: "Reviews",     key: (p, r) => r ? `${r.count} reviews` : "—" },
  { label: "Description", key: (p)    => <span className="text-xs text-gray-600 leading-relaxed">{p.description || "—"}</span> },
  { label: "Available",   key: (p)    => p.status === "available" || p.available !== false },
  { label: "Tags",        key: (p)    => p.tags?.join(", ") || p.tag || "—" },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

const ComparePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart, toggleWishlist, wishlist } = useAppContext();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [addedId, setAddedId]       = useState(null);

  // Selected IDs from URL
  const idsParam = searchParams.get("ids") || "";
  const selectedIds = idsParam ? idsParam.split(",").map(Number).filter(Boolean).slice(0, MAX_COMPARE) : [];

  const setIds = (ids) => {
    if (ids.length === 0) {
      searchParams.delete("ids");
    } else {
      searchParams.set("ids", ids.join(","));
    }
    setSearchParams(searchParams);
  };

  const addId   = (id) => setIds([...selectedIds, id]);
  const removeId = (id) => setIds(selectedIds.filter(x => x !== id));

  const { data: allProducts = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: fetchAllProducts,
  });

  // Fetch reviews for each selected product
  const [reviewMap, setReviewMap] = useState({});
  useEffect(() => {
    selectedIds.forEach(async id => {
      if (reviewMap[id]) return;
      try {
        const { data } = await axios.get(`${apiUrl}/reviews/item/${id}`);
        setReviewMap(prev => ({ ...prev, [id]: data.stats || null }));
      } catch {
        setReviewMap(prev => ({ ...prev, [id]: null }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(",")]);

  const selectedProducts = selectedIds
    .map(id => allProducts.find(p => p.id === id || p.id === String(id)))
    .filter(Boolean);

  const handleAddToCart = (product) => {
    addToCart({ ...product, qty: 1 });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1800);
  };

  const slots = [
    ...selectedProducts,
    ...Array(Math.max(0, MAX_COMPARE - selectedProducts.length)).fill(null),
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/menu" className="p-2 hover:bg-gray-100 rounded-xl transition text-gray-500">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900">Compare Items</h1>
              <p className="text-xs text-gray-400">Up to {MAX_COMPARE} items side by side</p>
            </div>
          </div>
          {selectedProducts.length > 0 && (
            <button
              onClick={() => setIds([])}
              className="text-sm text-gray-400 hover:text-red-500 flex items-center gap-1.5 transition"
            >
              <X size={14} /> Clear all
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Empty state */}
        {selectedProducts.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Star size={28} className="text-orange-500" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Nothing to compare yet</h2>
            <p className="text-gray-500 mb-6">Add up to {MAX_COMPARE} menu items to compare them side by side.</p>
            <button
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition"
            >
              <Plus size={18} /> Add Items
            </button>
          </div>
        )}

        {selectedProducts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-x-3">
              <thead>
                <tr>
                  {/* Row label column */}
                  <th className="w-36 min-w-[120px]" />

                  {/* Product columns */}
                  {slots.map((product, i) => (
                    <th key={i} className="min-w-[200px] align-top pb-4">
                      {product ? (
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                          {/* Remove button */}
                          <div className="relative">
                            <img
                              src={product.images?.image || product.image}
                              alt={product.name}
                              className="w-full h-40 object-cover"
                            />
                            <button
                              onClick={() => removeId(product.id)}
                              className="absolute top-2 right-2 w-7 h-7 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow transition"
                            >
                              <X size={14} className="text-gray-600" />
                            </button>
                            {wishlist.some(w => w.id === product.id) && (
                              <div className="absolute top-2 left-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                                <Heart size={13} className="text-white fill-white" />
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <p className="text-xs text-orange-500 font-semibold uppercase tracking-wide mb-0.5">
                              {product.category?.name}
                            </p>
                            <h3 className="font-extrabold text-gray-900 text-base leading-tight mb-1">
                              {product.name}
                            </h3>
                            <p className="text-xl font-black text-orange-500 mb-3">{product.price}</p>

                            {/* Actions */}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAddToCart(product)}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-bold transition ${
                                  addedId === product.id
                                    ? "bg-green-500 text-white"
                                    : "bg-orange-500 hover:bg-orange-600 text-white"
                                }`}
                              >
                                <ShoppingCart size={14} />
                                {addedId === product.id ? "Added!" : "Add to Cart"}
                              </button>
                              <button
                                onClick={() => toggleWishlist(product)}
                                className={`p-2 rounded-xl border transition ${
                                  wishlist.some(w => w.id === product.id)
                                    ? "bg-red-50 border-red-200 text-red-500"
                                    : "border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-400"
                                }`}
                              >
                                <Heart size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <EmptySlot onAdd={() => setPickerOpen(true)} />
                      )}
                    </th>
                  ))}

                  {/* Add button column — only if below max */}
                  {selectedProducts.length < MAX_COMPARE && (
                    <th className="min-w-[160px] align-top pb-4">
                      <EmptySlot onAdd={() => setPickerOpen(true)} />
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {ROWS.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? "" : ""}>
                    {/* Label */}
                    <td className="py-3 pr-2 align-middle">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{row.label}</span>
                    </td>

                    {/* Values */}
                    {slots.map((product, ci) => (
                      <td key={ci} className={`py-3 text-center align-middle rounded-xl ${ri % 2 === 0 ? "bg-white" : "bg-gray-50/80"} border-x border-gray-100 px-3`}>
                        {product ? (
                          <span className={`text-sm ${row.highlight ? "font-black text-orange-500 text-base" : "text-gray-700"}`}>
                            {fmt(row.key(product, reviewMap[product.id]))}
                          </span>
                        ) : (
                          <span className="text-gray-200 text-sm">—</span>
                        )}
                      </td>
                    ))}

                    {/* Filler for add-slot column */}
                    {selectedProducts.length < MAX_COMPARE && (
                      <td className={`py-3 ${ri % 2 === 0 ? "bg-white" : "bg-gray-50/80"} border-x border-gray-100`} />
                    )}
                  </tr>
                ))}

                {/* View detail row */}
                <tr>
                  <td />
                  {slots.map((product, ci) => (
                    <td key={ci} className="pt-4 text-center">
                      {product && (
                        <Link
                          to={`/menu/${product.id}`}
                          className="inline-block text-xs text-orange-500 hover:underline font-semibold"
                        >
                          View full details →
                        </Link>
                      )}
                    </td>
                  ))}
                  {selectedProducts.length < MAX_COMPARE && <td />}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product Picker Modal */}
      {pickerOpen && (
        <ProductPicker
          products={allProducts}
          selected={selectedIds}
          onAdd={addId}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <Footer />
    </div>
  );
};

export default ComparePage;
